import { useQuery } from "@tanstack/react-query";
import { addDays } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";

import { useSuperTokensUserId } from "@/authentication/AuthenticationStateProvider";
import { toUnixTimestamp } from "@/helpers/date";
import { isDefined } from "@/helpers/util";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { getLocalStorageValue, updateLocalStorage } from "@/hooks/useLocalStorage";
import { useGeneralQueries } from "@/queries/general/queryOptions";

import { useConfig } from "./config/useConfig";

declare global {
  interface Window {
    Intercom: { (key: string, ...args: any[]): void; q: any[]; c: (args: IArguments) => void };
    intercomSettings: IntercomMessengerSettings & IntercomDataAttributes;
  }
}

interface IntercomMessengerSettings {
  app_id: string;
  api_base: string;
  custom_launcher_selector?: string;
  alignment?: "left" | "right";
  vertical_padding?: number;
  horizontal_padding?: number;
  z_index?: number;
  hide_default_launcher?: boolean;
  hide_notifications?: boolean;
  theme_mode?: "light" | "dark" | "system";
  session_duration?: number;
  action_color?: string;
  background_color?: string;
}

interface IntercomDataAttributes {
  email: string;
  user_id: string;
  created_at?: number;
  name: string;
  phone?: string;
  language_override?: string;
  user_hash: string;
  company?: {
    id: string;
    name: string;
  };
  companies?: {
    company_id: string;
    name: string;
  }[];
  avatar?: {
    type: "avatar";
    image_url: string;
  };

  // custom attributes
  Role?: string;
  "Role Name"?: string;
  Maintainer?: string;
  Owner?: string;
  "Project Type"?: string;
}

interface IntercomTokenCache {
  expires: number;
  hmac: string;
}

/** Synchronizes the root dashboard user and route with Intercom without blocking UI. */
export function IntercomIntegration(): null {
  const superTokensUserId = useSuperTokensUserId();
  const user = useSessionUser();
  const generalQueries = useGeneralQueries();
  const intercomDashboardAppId = useConfig("intercomDashboardAppId");
  const envLongName = useConfig("envLongName");
  const intercomLocalStorageKey = `${envLongName}.intercom-security-st.${user.email}`;
  const [cachedIntercomToken, setCachedIntercomToken] = useState<string>();
  const [enabled, enabledHandlers] = useBool();
  const isBooted = useRef(false);
  const location = useLocation();

  useEffect(() => {
    if (!envLongName || !isDefined(intercomDashboardAppId) || user.role.isResidentType) {
      return;
    }

    const value = getLocalStorageValue<IntercomTokenCache | undefined>(intercomLocalStorageKey, undefined);
    if (value && new Date(value.expires) < new Date()) {
      // oxlint-disable-next-line react-hooks/set-state-in-effect
      setCachedIntercomToken(value.hmac);
    } else {
      enabledHandlers.setTrue();
    }
  }, [enabledHandlers, envLongName, intercomDashboardAppId, intercomLocalStorageKey, user.role.isResidentType]);

  const { data } = useQuery({
    ...generalQueries.intercomIdentity(),
    enabled,
  });

  useEffect(() => {
    if (data?.hmac) {
      updateLocalStorage(intercomLocalStorageKey, {
        expires: addDays(new Date(), 3).valueOf(),
        hmac: data.hmac,
      });
      enabledHandlers.setFalse();
    }
  }, [data?.hmac, enabledHandlers, intercomLocalStorageKey]);

  const token = cachedIntercomToken ?? data?.hmac;

  // Boot Intercom once when credentials are available, update on user data changes
  useEffect(() => {
    if (!intercomDashboardAppId || !superTokensUserId || !token) return;

    const settings: IntercomMessengerSettings & IntercomDataAttributes = {
      app_id: intercomDashboardAppId,
      api_base: "https://api-iam.intercom.io",
      name: user.fullName,
      email: user.email,
      user_id: superTokensUserId,
      user_hash: token,
      created_at: user.createdAt ? toUnixTimestamp(user.createdAt) : undefined,

      z_index: 30,
      action_color: "#003d80",
      background_color: "#f3f9f6",

      company: {
        id: user.project.id,
        name: user.project.name,
      },
      avatar: user.avatar
        ? {
            type: "avatar",
            image_url: user.avatar.url,
          }
        : undefined,
      "Project Type": user.project.type,
      Maintainer: user.project.maintenance,
      Owner: user.project.owner,
      Role: user.role.type,
      "Role Name": user.role.name,
    };

    if (!isBooted.current) {
      loadIntercomScript(intercomDashboardAppId);
      window.Intercom("boot", settings);
      isBooted.current = true;
    } else {
      window.Intercom("update", settings);
    }
  }, [intercomDashboardAppId, token, user, superTokensUserId]);

  // Notify Intercom on every route change so it can check for new messages/tours
  useEffect(() => {
    if (!isBooted.current) {
      return;
    }

    window.Intercom("update", { last_request_at: Math.floor(Date.now() / 1000) });
  }, [location.pathname]);

  return null;
}

// Load the Intercom script and set up the stub queue if not already present
function loadIntercomScript(appId: string): void {
  if (typeof window.Intercom !== "function") {
    const i = function () {
      // oxlint-disable-next-line prefer-rest-params
      (i as unknown as { c: (args: IArguments) => void }).c(arguments);
    };
    i.q = [] as any[];
    i.c = function (args: IArguments) {
      i.q.push(args);
    };
    window.Intercom = i;
  }

  if (document.querySelector(`script[src="https://widget.intercom.io/widget/${appId}"]`)) return;

  const script = document.createElement("script");
  script.type = "text/javascript";
  script.async = true;
  script.src = "https://widget.intercom.io/widget/" + appId;
  const firstScript = document.getElementsByTagName("script")[0];
  firstScript.parentNode!.insertBefore(script, firstScript);
}
